import { DataTable, useColumns } from "@/components/data-table";
import { DataFetcher } from "@/components/display";
import { ListPage } from "@/components/list-page";
import { ConfirmDialog, Drawer, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import CsvExportDialog from "@/features/export/components/csv-export-dialog";
import PrintExportDialog from "@/features/export/components/print-dialog-obs";
import XlsxExportDialog from "@/features/export/components/xlsx-export-dialog";
import { AddActividadComercialMutation } from "@/features/auxiliar/actividades-comerciales/api/add-actividad-comercial";
import { DeleteActividadComercialMutation } from "@/features/auxiliar/actividades-comerciales/api/delete-actividad-comercial";
import { EditActividadComercialMutation } from "@/features/auxiliar/actividades-comerciales/api/edit-actividad-comercial";
import { getAllActividadesComerciales, useGetAllActividadesComercalesQuery } from "@/features/auxiliar/actividades-comerciales/api/get-all-actividades-comerciales";
import { GetActividadComercialQuery } from "@/features/auxiliar/actividades-comerciales/api/get-actividad-comercial";
import { formatDate, formatString } from "@/shared/formats";
import { notify } from "@/shared/notification";
import { Permiso } from "@/shared/types/permiso";
import { useFilters } from "@/components/filters";
import { SchemaFormHook } from "@/components/form";
import ActividadComercialForm, { actividadComercialFormSchema } from "@/features/auxiliar/actividades-comerciales/components/actividad-comercial-form";

export default function ActividadesComercalesPage() {
  const openModal = useOpenModal();

  const hasWritePermiso = useHasPermisos(Permiso.WriteActividadesComerciales);

  const filterData = useFilters({
    filters: [
      { type: "string", label: "Código", name: "codigo" },
      { type: "string", label: "Nombre", name: "nombre" },
      { type: "pagination" }
    ]
  });

  const fetchData = useGetAllActividadesComercalesQuery(filterData.filterData);

  const columnData = useColumns({
    columns: [
      { key: "codigo", header: "Código", render: row => row.codigo, width: "auto" },
      { key: "nombre", header: "Nombre", render: row => row.nombre },
      { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
      { key: "fechaCreacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: false },
      { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
      { key: "fechaModificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false },
    ],
    key: "actividades_comerciales"
  });

  return <ListPage>
    <ListPage.Header>
      <ListPage.Header.Actions title="Actividades Comerciales">
        <ListPage.Header.Actions.FilterButton />
        <ListPage.Header.Actions.ColumnButton columnData={columnData} />
        {hasWritePermiso && <ListPage.Header.Actions.AddButton onClick={() => openModal("add")} />}
        <ListPage.Header.Actions.Divider />
        <ListPage.Header.Actions.ExportButton onClick={() => openModal("print")}>
          <ListPage.Header.Actions.ExportButton.Print onClick={() => openModal("print")} />
          <ListPage.Header.Actions.ExportButton.Csv onClick={() => openModal("csv")} />
          <ListPage.Header.Actions.ExportButton.Xlsx onClick={() => openModal("xlsx")} />
        </ListPage.Header.Actions.ExportButton>
      </ListPage.Header.Actions>
      <ListPage.Header.Filters filterData={filterData} />
    </ListPage.Header>
    <DataTable flex={1}>
      <DataTable.Content
        rows={fetchData.data?.result}
        columns={[
          ...columnData.columns,
          hasWritePermiso && {
            header: "Acción", width: "auto", align: "center", render: row => <DataTable.EntryOption>
              <DataTable.EntryOption.EditButton onClick={() => openModal("edit", row.id)} />
              <DataTable.EntryOption.DeleteButton onClick={() => openModal("delete", row)} />
            </DataTable.EntryOption>
          }
        ]}
        fit="flex"
        loading={fetchData.isFetching}
        error={fetchData.error}
      />
      <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
    </DataTable>

    <Drawer code="add">
      <ModalHeader>Agregar Actividad Comercial</ModalHeader>
      <AddActividadComercialMutation>
        {mutation => <SchemaFormHook
          schema={actividadComercialFormSchema}
          onSubmit={async ({ value }) => {
            await mutation.mutateAsync(value);
            notify("La actividad comercial se ha agregado correctamente.");
          }}
          submitOnce
          closeModalOnSubmit
        >
          {form => <ActividadComercialForm form={form} flex={1} padding={3} />}
        </SchemaFormHook>}
      </AddActividadComercialMutation>
    </Drawer>

    <Drawer code="edit">
      <ModalHeader>Editar Actividad Comercial</ModalHeader>
      <ModalValueExtractor>
        {actividadComercialId => <GetActividadComercialQuery id={actividadComercialId}>
          {query => <DataFetcher flex={1} query={query}>
            {actividadComercial => <EditActividadComercialMutation id={actividadComercial.id}>
              {mutation => <SchemaFormHook
                schema={actividadComercialFormSchema}
                onSubmit={async ({ value }) => {
                  await mutation.mutateAsync(value);
                  notify("La actividad comercial se ha editado correctamente.");
                }}
                defaultValues={{ ...actividadComercial }}
                submitOnce
                closeModalOnSubmit
              >
                {form => <ActividadComercialForm form={form} flex={1} padding={3} />}
              </SchemaFormHook>}
            </EditActividadComercialMutation>}
          </DataFetcher>}
        </GetActividadComercialQuery>}
      </ModalValueExtractor>
    </Drawer>

    <ConfirmDialog code="delete">
      <ModalValueExtractor>
        {actividadComercial => <DeleteActividadComercialMutation id={actividadComercial.id}>
          {mutation => <ConfirmDialog.Content
            title={`Borrar Actividad Comercial "${actividadComercial.nombre}"`}
            description={`¿Estás seguro que quieres borrar la actividad comercial "${actividadComercial.nombre}"?`}
            onConfirm={async () => {
              await mutation.mutateAsync();
              notify(`La actividad comercial "${actividadComercial.nombre}" se ha borrado correctamente.`, { variant: "success" });
            }}
          />}
        </DeleteActividadComercialMutation>}
      </ModalValueExtractor>
    </ConfirmDialog>

    <PrintExportDialog
      code="print"
      columns={columnData.columns}
      filterData={filterData}
      fetchFn={getAllActividadesComerciales}
      defaultFileTitle="Actividades Comerciales"
    />

    <CsvExportDialog
      code="csv"
      columns={columnData.columns}
      filterData={filterData}
      fetchFn={getAllActividadesComerciales}
      defaultFileTitle="Actividades Comerciales"
    />

    <XlsxExportDialog
      code="xlsx"
      columns={columnData.columns}
      filterData={filterData}
      fetchFn={getAllActividadesComerciales}
      defaultFileTitle="Actividades Comerciales"
    />
  </ListPage>
}
